import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, rigLabel } from "./serviceShared";
import type { useServiceBoardPanel } from "./useServiceBoardPanel";
import { serviceJobKindLabel } from "@/lib/service/serviceJobKind";
import type { ServiceEvent, ServiceRig } from "@/lib/service/types";

type Props = {
  d: ReturnType<typeof useServiceBoardPanel>;
  embedded?: boolean;
};

function formatRigCapacity(rig: ServiceRig): string | null {
  if (rig.capacity_value === null || !rig.capacity_unit) return null;
  return `${rig.capacity_value} ${rig.capacity_unit.replace(/_/g, " ")}`;
}

function eventChipLabel(event: ServiceEvent, rigs: ServiceRig[]): string {
  const kind = serviceJobKindLabel(event.job_kind);
  const contacted = event.customer_contacted_at ? " · contacted" : "";
  const base = event.title || event.location_label || event.id.slice(0, 8);
  if (event.required_rig_id) {
    return `${kind} · ${base} → ${rigLabel(rigs, event.required_rig_id)}${contacted}`;
  }
  return `${kind} · ${base}${contacted}`;
}

export function ServiceDispatchBoard({ d, embedded = false }: Props) {
  const unassigned = d.events.filter(
    (e) => e.status !== "complete" && e.status !== "cancelled" && !e.assigned_rig_id
  );

  const body = (
    <>
      <p className="text-xs text-gray-600 dark:text-zinc-400 mb-3">
        Drag an unassigned event onto a rig row to assign tech + equipment. Events with a required rig
        must land on a matching rig; same rig + date double-booking is blocked.
      </p>

      {d.assignError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {d.assignError}
          <button type="button" className={`${ghostBtnClass} ml-2 text-[11px]`} onClick={d.clearAssignFeedback}>
            Dismiss
          </button>
        </div>
      ) : null}

      {d.assignWarnings.length > 0 ? (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
          <div className="font-semibold mb-1">Assignment warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {d.assignWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <button type="button" className={`${ghostBtnClass} mt-2 text-[11px]`} onClick={d.clearAssignFeedback}>
            Dismiss
          </button>
        </div>
      ) : null}

      {d.rigs.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No active rigs yet. Add fleet in <strong>Service setup → Shared fleet</strong>.
        </div>
      ) : (
        <ul className="space-y-3">
          {d.rigs.map((rig) => {
            const assigned = d.events.filter((e) => e.assigned_rig_id === rig.id);
            const capacity = formatRigCapacity(rig);
            const busy = d.assignBusyRigId === rig.id;
            return (
              <li
                key={rig.id}
                className="rounded-xl border border-dashed border-gray-300 bg-gray-50/80 px-3 py-2 dark:border-zinc-600 dark:bg-zinc-900/40"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const eventId = e.dataTransfer.getData("text/service-event-id");
                  if (eventId) void d.assignDispatch(eventId, rig.id, rig.standard_tech_id);
                }}
              >
                <div className="flex flex-wrap items-baseline gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
                  <span>{rig.designation}</span>
                  <span className="text-xs font-normal text-gray-600 dark:text-zinc-400">
                    {rig.rig_kind}
                  </span>
                  {capacity ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide rounded bg-gray-200 px-1.5 py-0.5 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                      {capacity}
                    </span>
                  ) : (
                    <span className="text-[10px] italic text-amber-700 dark:text-amber-300">
                      no capacity on file
                    </span>
                  )}
                  {busy ? (
                    <span className="text-xs text-gray-500 dark:text-zinc-400">Assigning…</span>
                  ) : null}
                </div>
                {assigned.length > 0 ? (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {assigned.map((ev) => (
                      <li
                        key={ev.id}
                        className="rounded-md bg-white border border-gray-200 px-2 py-0.5 text-xs dark:bg-zinc-800 dark:border-zinc-600"
                      >
                        {eventChipLabel(ev, d.rigs)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-1 text-xs italic text-gray-500 dark:text-zinc-500">
                    Drop an event here
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unassigned.length > 0 ? (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
            Unassigned ({unassigned.length})
          </div>
          <ul className="flex flex-wrap gap-2">
            {unassigned.map((ev) => (
              <li key={ev.id}>
                <EventChip event={ev} rigs={d.rigs} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {d.localError ? (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {d.localError}
          <button type="button" className={`${ghostBtnClass} ml-2 text-[11px]`} onClick={() => d.reload()}>
            Retry
          </button>
        </div>
      ) : null}
    </>
  );

  if (embedded) return body;
  return <SectionCard title="Equipment dispatch">{body}</SectionCard>;
}

function EventChip({ event, rigs }: { event: ServiceEvent; rigs: ServiceRig[] }) {
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/service-event-id", event.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="inline-block cursor-grab rounded-lg border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-900 active:cursor-grabbing dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
      title={
        event.required_rig_id
          ? `Requires ${rigLabel(rigs, event.required_rig_id)} — drag onto a rig row`
          : "Drag onto a rig row"
      }
    >
      {eventChipLabel(event, rigs)}
    </span>
  );
}
